import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Clock, TrendingDown, Calculator, CheckCircle } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Button } from '../components/ui/button';
import { Card, CardHeader, CardTitle, CardDescription } from '../components/ui/card';

const Restnutzungsdauer = () => {
  const benefits = [
    'Höhere Abschreibungen durch realistische Restnutzungsdauer',
    'Geringere steuerliche Belastung',
    'Fundierte Bewertung der tatsächlichen Bausubstanz',
    'Steuerlich anerkannte Gutachten',
    'Optimierung Ihrer Immobilieninvestments',
  ];

  return (
    <div className="min-h-screen bg-white">
      <Header />

      {/* Hero Section */}
      <section className="relative pt-32 pb-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="flex flex-col">
              <div className="space-y-6">
              <div className="inline-flex items-center space-x-2 text-brand-700 font-medium">
                <Clock className="w-5 h-5" />
                <span>Restnutzungsdauer</span>
              </div>
              <h1 className="text-4xl md:text-5xl font-bold text-brand-900 leading-tight">
                Restnutzungsdauer, die steuerlich wirkt
              </h1>
              <p className="text-xl text-brand-700 leading-relaxed">
                Fokus auf höhere Abschreibungen und geringere steuerliche Belastung durch 
                tatsächliche Substanzbewertung.
              </p>
              </div>
              <div className="mt-10">
              <Link to="/kontakt">
                <Button 
                  size="lg" 
                  className="bg-brand-900 hover:bg-brand-800 text-white px-8 py-3 text-base transition-all duration-300 hover:shadow-lg"
                >
                  Beratung anfragen
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </Link>
              </div>
            </div>
            <div className="relative h-[500px] rounded-2xl overflow-hidden shadow-2xl">
              <img
                src="https://images.unsplash.com/photo-1503387762-592deb58ef4e"
                alt="Architekturpläne und Arbeitsplatz"
                className="w-full h-full object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-brand-50">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-4xl font-bold text-brand-900 mb-6">
                Steuerliche Vorteile nutzen
              </h2>
              <p className="text-lg text-brand-600 leading-relaxed mb-8">
                Die korrekte Ermittlung der Restnutzungsdauer ist entscheidend für die steuerliche 
                Optimierung Ihrer Immobilie. Unsere Gutachten basieren auf fundierten technischen 
                Analysen und sind von Finanzämtern anerkannt.
              </p>
              <div className="space-y-4">
                {benefits.map((benefit, index) => (
                  <div key={index} className="flex items-start space-x-3">
                    <CheckCircle className="w-6 h-6 text-brand-700 flex-shrink-0 mt-0.5" />
                    <span className="text-brand-700 text-base">{benefit}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
              <Card className="border-2 border-brand-200 hover:border-brand-400 transition-all duration-300 hover:shadow-lg">
                <CardHeader className="p-6">
                  <TrendingDown className="w-10 h-10 text-brand-700 mb-4" />
                  <CardTitle className="text-xl mb-2">Steueroptimierung</CardTitle>
                  <CardDescription className="text-brand-600">
                    Maximale Abschreibungsmöglichkeiten durch realistische Bewertung der Bausubstanz
                  </CardDescription>
                </CardHeader>
              </Card>
              <Card className="border-2 border-brand-200 hover:border-brand-400 transition-all duration-300 hover:shadow-lg">
                <CardHeader className="p-6">
                  <Calculator className="w-10 h-10 text-brand-700 mb-4" />
                  <CardTitle className="text-xl mb-2">Präzise Berechnung</CardTitle>
                  <CardDescription className="text-brand-600">
                    Fundierte technische Analysen für finanzamtgerechte Gutachten
                  </CardDescription>
                </CardHeader>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-4xl font-bold text-brand-900 mb-12 text-center">
            Unser Vorgehen
          </h2>
          <div className="space-y-8">
            <div className="flex items-start space-x-6 p-6 bg-brand-50 rounded-xl">
              <div className="flex-shrink-0 w-12 h-12 bg-brand-900 text-white rounded-full flex items-center justify-center font-bold text-lg">
                1
              </div>
              <div>
                <h3 className="text-xl font-bold text-brand-900 mb-2">Bestandsaufnahme</h3>
                <p className="text-brand-600">
                  Detaillierte Analyse der Bausubstanz und des aktuellen Zustands der Immobilie
                </p>
              </div>
            </div>
            <div className="flex items-start space-x-6 p-6 bg-brand-50 rounded-xl">
              <div className="flex-shrink-0 w-12 h-12 bg-brand-900 text-white rounded-full flex items-center justify-center font-bold text-lg">
                2
              </div>
              <div>
                <h3 className="text-xl font-bold text-brand-900 mb-2">Technische Bewertung</h3>
                <p className="text-brand-600">
                  Ermittlung der tatsächlichen Restnutzungsdauer basierend auf Baujahr, Bauweise und Erhaltungszustand
                </p>
              </div>
            </div>
            <div className="flex items-start space-x-6 p-6 bg-brand-50 rounded-xl">
              <div className="flex-shrink-0 w-12 h-12 bg-brand-900 text-white rounded-full flex items-center justify-center font-bold text-lg">
                3
              </div>
              <div>
                <h3 className="text-xl font-bold text-brand-900 mb-2">Gutachtenerstellung</h3>
                <p className="text-brand-600">
                  Erstellung eines finanzamtgerechten Gutachtens mit detaillierter Begründung
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-brand-50">
        <div className="max-w-4xl mx-auto text-center flex flex-col items-center gap-8">
          <h2 className="text-3xl md:text-4xl font-bold text-brand-900">
            Optimieren Sie Ihre Steuerbelastung
          </h2>
          <p className="text-xl text-brand-600 max-w-3xl">
            Lassen Sie uns gemeinsam die steuerlichen Möglichkeiten Ihrer Immobilie analysieren.
          </p>
          <Link to="/kontakt">
            <Button 
              size="lg" 
              className="bg-brand-900 hover:bg-brand-800 text-white px-10 py-3 text-lg transition-all duration-300 hover:shadow-lg"
            >
              Kostenlose Erstberatung
              <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Restnutzungsdauer;
